import type { UserProfile } from "@/api/types";

export type NavItem = {
  label: string;
  path: string;
  permission: string | null;
  caption: string;
};

export const NAV_ITEMS: NavItem[] = [
  { label: "Dashboard", path: "/", permission: null, caption: "Cross-platform KPIs for sales and claims." },
  { label: "Clients", path: "/clients", permission: "crm.view", caption: "Client 360 — household, policies, claims, and activity." },
  { label: "My Book", path: "/my-book", permission: "crm.view", caption: "Your book of business — clients, renewals, and open claims." },
  { label: "My Work", path: "/my-work", permission: "tasks.view", caption: "Unified inbox for client service and claim investigations." },
  { label: "Claims", path: "/claims", permission: "claims.view", caption: "FNOL through triage, review, SLA, and payment." },
  { label: "Pipeline", path: "/pipeline", permission: "crm.view", caption: "Lead stages from first contact through bind." },
  { label: "Rate Shop", path: "/rate-shop", permission: "crm.view", caption: "Multi-carrier quote comparison." },
  { label: "Quotes", path: "/quotes", permission: "crm.view", caption: "Active quotes and follow-up status." },
  { label: "Submissions", path: "/submissions", permission: "crm.view", caption: "Carrier submissions from draft through quote, bind, or decline." },
  { label: "Policies", path: "/policies", permission: "crm.view", caption: "In-force policies and coverage details." },
  { label: "Renewals", path: "/renewals", permission: "crm.view", caption: "90-day renewal pipeline with remarket triggers and batch outreach." },
  { label: "Documents", path: "/documents", permission: "documents.view", caption: "Unified document library." },
  { label: "Commissions", path: "/commissions", permission: "commissions.view", caption: "Producer statements and agency commission rollup." },
  { label: "Accounting", path: "/accounting", permission: "accounting.view", caption: "Client invoices, receivables aging, and the agency ledger." },
  { label: "QuickBooks", path: "/settings/quickbooks", permission: "accounting.manage", caption: "Sync clients, invoices, and payments to QuickBooks." },
  { label: "Reports", path: "/reports", permission: "reports.view", caption: "Pipeline, producer, and renewal snapshots." },
  { label: "Admin", path: "/admin", permission: "users.manage", caption: "Users, agency config, automation, and fraud rules." },
  { label: "Billing", path: "/settings/billing", permission: "agency.manage", caption: "Subscription plan, seats, and carrier rater limits." },
  {
    label: "Data migration",
    path: "/settings/data-migration",
    permission: "agency.manage",
    caption: "Export and import clients/policies (AMS360, Applied Epic, native CSV).",
  },
  { label: "Settings", path: "/settings", permission: null, caption: "Account and connections." },
];

function isOperationsUser(user: UserProfile | null): boolean {
  if (!user) return false;
  const agencyRole = user.role.toLowerCase();
  const reviewerRole = (user.legacy_reviewer_role || "").toLowerCase();
  if (["admin", "supervisor"].includes(reviewerRole)) return true;
  if (["admin", "agency_owner"].includes(agencyRole)) return true;
  return user.permissions.some((p) => ["agency.manage", "platform.admin"].includes(p));
}

export function canReviewClaims(user: UserProfile | null): boolean {
  if (!user) return false;
  const reviewerRole = (user.legacy_reviewer_role || "").toLowerCase();
  if (["reviewer", "supervisor", "siu", "legal", "admin"].includes(reviewerRole)) return true;
  return user.permissions.includes("claims.review");
}

export function canSuperviseClaims(user: UserProfile | null): boolean {
  if (!user) return false;
  const reviewerRole = (user.legacy_reviewer_role || "").toLowerCase();
  if (["supervisor", "admin"].includes(reviewerRole)) return true;
  return user.permissions.some((p) => ["agency.manage", "platform.admin"].includes(p));
}

export function canManageUsers(user: UserProfile | null): boolean {
  if (!user) return false;
  if (user.permissions.includes("users.manage")) return true;
  return isOperationsUser(user);
}

export function canManageAgency(user: UserProfile | null): boolean {
  if (!user) return false;
  if (user.permissions.some((p) => ["agency.manage", "platform.admin", "users.manage"].includes(p))) return true;
  return isOperationsUser(user);
}

export function canAccess(user: UserProfile | null, permission: string | null): boolean {
  if (permission === null) return true;
  if (!user) return false;
  if (permission === "__operations__") return isOperationsUser(user);

  const perms = new Set(user.permissions);
  if (perms.size === 0) {
    return ["crm.view", "claims.view", "documents.view", "tasks.view", "reports.view", "commissions.view"].includes(
      permission,
    );
  }
  if (permission === "documents.view") {
    return perms.has("crm.view") || perms.has("claims.view") || perms.has("documents.upload");
  }
  if (permission === "tasks.view") {
    return perms.has("crm.edit") || perms.has("claims.review");
  }
  if (permission === "commissions.view") {
    return perms.has("crm.commissions.view") || perms.has("crm.view");
  }
  if (permission === "users.manage") {
    return perms.has("users.manage") || isOperationsUser(user);
  }
  if (permission === "agency.manage") {
    return perms.has("agency.manage") || isOperationsUser(user);
  }
  return perms.has(permission);
}

export function visibleNavItems(user: UserProfile | null): NavItem[] {
  return NAV_ITEMS.filter((item) => canAccess(user, item.permission));
}

export type NavAccessGap = {
  label: string;
  path: string;
  reason: string;
};

export function hiddenNavItems(user: UserProfile | null): NavAccessGap[] {
  if (!user) return [];
  return NAV_ITEMS.filter((item) => !canAccess(user, item.permission)).map((item) => ({
    label: item.label,
    path: item.path,
    reason: item.permission ? `Requires permission: ${item.permission}` : "Restricted",
  }));
}

export function roleLabel(user: UserProfile | null): string {
  if (!user) return "Guest";
  const agency = user.role.replace(/_/g, " ");
  const legacy = user.legacy_reviewer_role?.replace(/_/g, " ");
  if (legacy && legacy !== agency) return `${agency} · ${legacy}`;
  return agency;
}

export function defaultLandingPath(user: UserProfile | null): string {
  if (!user) return "/login";
  const agencyRole = user.role.toLowerCase();
  const reviewerRole = (user.legacy_reviewer_role || "").toLowerCase();
  const perms = new Set(user.permissions);

  if (["producer", "csr"].includes(agencyRole) && canAccess(user, "crm.view")) {
    return "/my-book";
  }
  if (
    (agencyRole === "claims_rep" || ["reviewer", "siu", "legal"].includes(reviewerRole)) &&
    canAccess(user, "tasks.view")
  ) {
    return "/my-work";
  }
  if (["admin", "supervisor"].includes(reviewerRole) || ["admin", "agency_owner"].includes(agencyRole)) {
    return "/";
  }
  if (perms.has("crm.view") && !perms.has("claims.review")) return "/my-book";
  if (perms.has("claims.review")) return "/my-work";
  return "/";
}
